import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ArrowUpRight, Clock, ChevronLeft } from "lucide-react";
import {
  ASK_ARIF_CATEGORIES,
  articlesByCategory,
  getCategory,
} from "@/lib/askArif";
import { categoryOgImage, sectionImageMeta } from "@/lib/ogSections";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

export const Route = createFileRoute("/ask-arif/category/$category")({
  loader: ({ params }) => {
    const category = getCategory(params.category);
    if (!category) throw notFound();
    return { category, articles: articlesByCategory(params.category) };
  },
  head: ({ loaderData, params }) => {
    const c = loaderData?.category;
    if (!c) return {};
    const title = `${c.title} — Ask Arif Knowledge Center`;
    const url = `https://arifkhanglobal.com/ask-arif/category/${params.category}`;
    return {
      meta: [
        { title },
        { name: "description", content: `${c.short} Practical insights from Arif Khan on ${c.title}.` },
        { property: "og:title", content: title },
        { property: "og:description", content: c.short },
        { property: "og:url", content: url },
        ...sectionImageMeta(categoryOgImage(params.category), `${c.title} — Ask Arif by Arif Khan`),
      ],
      links: [{ rel: "canonical", href: url }],

      scripts: [
        pageSchemaScript(
          {
            path: `/ask-arif/category/${params.category}`,
            type: "CollectionPage",
            name: title,
            description: `${c.short} Practical insights from Arif Khan on ${c.title}.`,
            breadcrumb: [
              { name: "Ask Arif", url: "/ask-arif" },
              { name: c.title, url: `/ask-arif/category/${params.category}` },
            ],
            extra: { about: { "@id": PERSON_ID } },
          },
          [
            {
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en-IN",
              isPartOf: { "@id": `${url}#webpage` },
              mainEntity: (loaderData?.articles ?? []).map((a) => ({
                "@type": "Question",
                name: a.question,
                url: `https://arifkhanglobal.com/ask-arif/${a.slug}#short-answer`,
                acceptedAnswer: { "@type": "Answer", text: a.shortAnswer },
              })),
            },
          ],
        ),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-3xl font-semibold">Category not found</h1>
      <Link to="/ask-arif" className="mt-6 inline-flex text-sm font-medium text-primary">
        Back to Ask Arif
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-semibold">This page didn't load</h1>
      <p className="mt-2 text-sm text-ink-soft">{error.message}</p>
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const data = Route.useLoaderData() as {
    category: (typeof ASK_ARIF_CATEGORIES)[number];
    articles: import("@/lib/askArif").AskArifArticle[];
  };
  const { category, articles } = data;

  return (
    <>
      <section className="relative overflow-hidden border-b border-border bg-surface pt-36 pb-20">
        <div aria-hidden className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[800px] rounded-full bg-gradient-to-br from-primary/15 via-accent/10 to-transparent blur-3xl"
        />
        <div className="container-page relative max-w-4xl">
          <Link
            to="/ask-arif"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-soft hover:text-primary transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> All categories
          </Link>
          <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <category.Icon className="h-6 w-6" strokeWidth={1.6} />
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            {category.title}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-ink-soft leading-relaxed">{category.short}</p>
        </div>
      </section>

      <section className="py-20 border-b border-border">
        <div className="container-page">
          {articles.length === 0 ? (
            <p className="text-sm text-ink-soft">More articles in this category are coming soon.</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((a, i) => (
                <motion.div
                  key={a.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to="/ask-arif/$slug"
                    params={{ slug: a.slug }}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background p-6 hover:border-primary/40 hover:shadow-[0_20px_60px_-30px_rgba(21,101,192,0.35)] transition-all"
                  >
                    <h3 className="text-lg font-semibold tracking-tight">{a.question}</h3>
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">
                      {a.shortAnswer}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {a.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-border text-ink-soft"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-6 flex items-center justify-between text-[11px] text-ink-soft">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" strokeWidth={1.8} />
                        {a.readingMinutes} min read
                      </span>
                      <ArrowUpRight className="h-4 w-4 group-hover:text-primary transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Short answers — citation-ready summaries for every article here */}
      {articles.length > 0 && (
        <section id="short-answers" className="scroll-mt-28 py-20 border-b border-border bg-surface">
          <div className="container-page max-w-4xl">
            <span className="eyebrow">Short answers</span>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
              {category.title} in one line each.
            </h2>
            <p className="mt-4 max-w-2xl text-sm text-ink-soft leading-relaxed">
              The headline answer to every question in this category, so you can scan the thinking
              before opening the full write-up.
            </p>
            <dl className="mt-10 grid gap-5">
              {articles.map((a) => (
                <div key={a.slug} className="rounded-2xl border border-border bg-background p-6">
                  <dt className="text-base font-semibold tracking-tight">
                    <Link
                      to="/ask-arif/$slug"
                      params={{ slug: a.slug }}
                      hash="short-answer"
                      className="hover:text-primary transition-colors"
                    >
                      {a.question}
                    </Link>
                  </dt>
                  <dd className="mt-2.5 text-sm text-ink-soft leading-relaxed">{a.shortAnswer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* Other categories */}
      <section className="py-20">
        <div className="container-page">
          <span className="eyebrow">Keep exploring</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
            Related topics in Ask Arif.
          </h2>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {ASK_ARIF_CATEGORIES.filter((c) => c.slug !== category.slug).map((c) => (
              <Link
                key={c.slug}
                to="/ask-arif/category/$category"
                params={{ category: c.slug }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm hover:border-primary/50 hover:text-primary transition-colors"
              >
                <c.Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                {c.title}
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
            >
              Let's Talk <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

